import React, { useState, useEffect } from 'react';
import {
  Plus,
  Edit2,
  Trash2,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Save,
  X,
  TrendingUp,
  TrendingDown,
  Calendar,
  FolderCog
} from 'lucide-react';
import { useAuth } from '../../AuthContext';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, setDoc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import CategoryManager from './CategoryManager';
import {
  getDefaultCategories,
  getCategoriesByGroup,
  getCategoryById
} from '../../utils/budgeting/categoryDefinitions';
import {
  normalizeToMonthly,
  formatBudgetAmount,
  getFrequencyOptions
} from '../../utils/budgeting/frequencyCalculations';
import {
  calculateMonthlySummary
} from '../../utils/budgeting/budgetCalculations';
import { formatCurrency } from '../../countryConfig';

const MonthlyBudget = ({ country, currency, onNavigate }) => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [budgetItems, setBudgetItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [expandedGroups, setExpandedGroups] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showCategoryManager, setShowCategoryManager] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [filterOwner, setFilterOwner] = useState(''); // Filter by family member
  const [formData, setFormData] = useState({
    name: '',
    categoryId: '',
    amount: '',
    frequency: 'monthly',
    owner: '',
    type: 'expense'
  });
  const [summary, setSummary] = useState(null);
  const [groupOrder, setGroupOrder] = useState({});

  useEffect(() => {
    if (currentUser) {
      loadBudgetData();
      loadCategories();
      loadGroupOrder();
    }
  }, [currentUser]);

  useEffect(() => {
    if (budgetItems.length > 0) {
      const summaryData = calculateMonthlySummary(budgetItems);
      setSummary(summaryData);
    }
  }, [budgetItems]);

  const loadBudgetData = async () => {
    try {
      setLoading(true);
      const budgetItemsRef = collection(db, `users/${currentUser.uid}/budgetItems`);
      const snapshot = await getDocs(budgetItemsRef);

      const items = [];
      snapshot.forEach(doc => {
        items.push({ id: doc.id, ...doc.data() });
      });

      setBudgetItems(items);
    } catch (error) {
      console.error('Error loading budget items:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      // Always start with default categories
      const defaultCats = getDefaultCategories();

      // Load user's custom categories
      const categoriesRef = collection(db, `users/${currentUser.uid}/categories`);
      const snapshot = await getDocs(categoriesRef);

      const customCats = [];
      snapshot.forEach(doc => {
        customCats.push({ id: doc.id, ...doc.data() });
      });

      // Merge default and custom categories
      const allCategories = [...defaultCats, ...customCats];
      setCategories(allCategories);

      // Expand all groups by default
      const groups = [...new Set(allCategories.map(c => c.group))];
      setExpandedGroups(groups);
    } catch (error) {
      console.error('Error loading categories:', error);
      // Fallback to defaults
      const cats = getDefaultCategories();
      setCategories(cats);
      const groups = [...new Set(cats.map(c => c.group))];
      setExpandedGroups(groups);
    }
  };

  const loadGroupOrder = async () => {
    try {
      const groupOrderRef = doc(db, `users/${currentUser.uid}/preferences/groupOrder`);
      const snapshot = await getDoc(groupOrderRef);

      if (snapshot.exists()) {
        setGroupOrder(snapshot.data().order || {});
      }
    } catch (error) {
      console.error('Error loading group order:', error);
    }
  };

  const saveGroupOrder = async (newOrder) => {
    try {
      const groupOrderRef = doc(db, `users/${currentUser.uid}/preferences/groupOrder`);
      await setDoc(groupOrderRef, { order: newOrder, updatedAt: new Date() });
      setGroupOrder(newOrder);
    } catch (error) {
      console.error('Error saving group order:', error);
      alert('Failed to save group order');
    }
  };

  const handleMoveGroup = async (groupName, direction) => {
    try {
      // Get all group names in current display order
      const groupEntries = Object.entries(groupedItems)
        .sort(([a], [b]) => {
          const orderA = groupOrder[a] !== undefined ? groupOrder[a] : 999;
          const orderB = groupOrder[b] !== undefined ? groupOrder[b] : 999;
          if (orderA !== orderB) return orderA - orderB;
          // Fallback to default sort
          if (a === 'Income') return -1;
          if (b === 'Income') return 1;
          if (a === 'Other') return 1;
          if (b === 'Other') return -1;
          return a.localeCompare(b);
        });

      const groupNames = groupEntries.map(([name]) => name);
      const currentIndex = groupNames.indexOf(groupName);

      if (currentIndex === -1) return;
      if (direction === 'up' && currentIndex === 0) return;
      if (direction === 'down' && currentIndex === groupNames.length - 1) return;

      const swapIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;

      // Create new order mapping
      const newOrder = { ...groupOrder };
      newOrder[groupNames[currentIndex]] = swapIndex;
      newOrder[groupNames[swapIndex]] = currentIndex;

      await saveGroupOrder(newOrder);
    } catch (error) {
      console.error('Error reordering group:', error);
    }
  };

  const handleAddItem = async (e) => {
    e.preventDefault();

    try {
      const category = categories.find(c => c.id === formData.categoryId);
      if (!category) {
        alert('Please select a category');
        return;
      }

      const amount = parseFloat(formData.amount);
      if (isNaN(amount) || amount <= 0) {
        alert('Please enter a valid amount');
        return;
      }

      const newItem = {
        name: formData.name,
        categoryId: formData.categoryId,
        categoryName: category.name,
        groupName: category.group,
        amount: amount,
        frequency: formData.frequency,
        monthlyEquivalent: normalizeToMonthly(amount, formData.frequency),
        owner: formData.owner || null,
        type: category.type,
        createdAt: new Date()
      };

      const budgetItemsRef = collection(db, `users/${currentUser.uid}/budgetItems`);
      await addDoc(budgetItemsRef, newItem);

      // Reload data
      await loadBudgetData();

      // Reset form
      setFormData({
        name: '',
        categoryId: '',
        amount: '',
        frequency: 'monthly',
        owner: '',
        type: 'expense'
      });
      setShowAddModal(false);
    } catch (error) {
      console.error('Error adding budget item:', error);
      alert('Failed to add budget item');
    }
  };

  const handleUpdateItem = async (e) => {
    e.preventDefault();

    try {
      const category = categories.find(c => c.id === formData.categoryId);
      const amount = parseFloat(formData.amount);

      const updates = {
        name: formData.name,
        categoryId: formData.categoryId,
        categoryName: category.name,
        groupName: category.group,
        amount: amount,
        frequency: formData.frequency,
        monthlyEquivalent: normalizeToMonthly(amount, formData.frequency),
        owner: formData.owner || null,
        type: category.type
      };

      const itemRef = doc(db, `users/${currentUser.uid}/budgetItems`, editingItem.id);
      await updateDoc(itemRef, updates);

      await loadBudgetData();

      setEditingItem(null);
      setFormData({
        name: '',
        categoryId: '',
        amount: '',
        frequency: 'monthly',
        owner: '',
        type: 'expense'
      });
      setShowAddModal(false);
    } catch (error) {
      console.error('Error updating budget item:', error);
      alert('Failed to update budget item');
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (!window.confirm('Are you sure you want to delete this budget item?')) {
      return;
    }

    try {
      const itemRef = doc(db, `users/${currentUser.uid}/budgetItems`, itemId);
      await deleteDoc(itemRef);
      await loadBudgetData();
    } catch (error) {
      console.error('Error deleting budget item:', error);
      alert('Failed to delete budget item');
    }
  };

  const handleEditItem = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name,
      categoryId: item.categoryId,
      amount: item.amount.toString(),
      frequency: item.frequency,
      owner: item.owner || '',
      type: item.type
    });
    setShowAddModal(true);
  };

  const toggleGroup = (groupName) => {
    setExpandedGroups(prev =>
      prev.includes(groupName)
        ? prev.filter(g => g !== groupName)
        : [...prev, groupName]
    );
  };

  const getGroupedItems = () => {
    const grouped = {};

    categories.forEach(cat => {
      if (!grouped[cat.group]) {
        grouped[cat.group] = {
          categories: [],
          items: [],
          total: 0
        };
      }
      grouped[cat.group].categories.push(cat);
    });

    // Filter by owner if selected
    const filteredItems = filterOwner
      ? budgetItems.filter(item => item.owner === filterOwner)
      : budgetItems;

    filteredItems.forEach(item => {
      const groupName = item.groupName || 'Other';
      if (!grouped[groupName]) {
        grouped[groupName] = {
          categories: [],
          items: [],
          total: 0
        };
      }
      grouped[groupName].items.push(item);
      grouped[groupName].total += normalizeToMonthly(item.amount, item.frequency);
    });

    return grouped;
  };

  // Get unique family members for filter
  const familyMembers = [...new Set(budgetItems.map(item => item.owner).filter(o => o))];

  const groupedItems = getGroupedItems();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Monthly Budget</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Manage your household income and expenses
          </p>
        </div>
        <div className="flex items-center gap-3">
          {familyMembers.length > 0 && (
            <select
              value={filterOwner}
              onChange={(e) => setFilterOwner(e.target.value)}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Family Members</option>
              {familyMembers.map(member => (
                <option key={member} value={member}>{member}</option>
              ))}
            </select>
          )}
          <button
            onClick={() => setShowCategoryManager(true)}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            <FolderCog className="w-4 h-4" />
            Manage Categories
          </button>
          <button
            onClick={() => {
              setEditingItem(null);
              setFormData({
                name: '',
                categoryId: '',
                amount: '',
                frequency: 'monthly',
                owner: '',
                type: 'expense'
              });
              setShowAddModal(true);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Budget Item
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-5 h-5 text-green-600" />
              <span className="text-sm font-medium text-green-900 dark:text-green-100">Income</span>
            </div>
            <p className="text-2xl font-bold text-green-600">{formatCurrency(summary.totalMonthlyIncome, country)}</p>
            <p className="text-xs text-green-700 dark:text-green-300 mt-1">per month</p>
          </div>

          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <TrendingDown className="w-5 h-5 text-red-600" />
              <span className="text-sm font-medium text-red-900 dark:text-red-100">Expenses</span>
            </div>
            <p className="text-2xl font-bold text-red-600">{formatCurrency(summary.totalMonthlyExpenses, country)}</p>
            <p className="text-xs text-red-700 dark:text-red-300 mt-1">per month</p>
          </div>

          <div className={`border rounded-lg p-4 ${
            summary.surplus >= 0
              ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
              : 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800'
          }`}>
            <div className="flex items-center gap-2 mb-2">
              <Calendar className="w-5 h-5 text-blue-600" />
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">Surplus</span>
            </div>
            <p className={`text-2xl font-bold ${summary.surplus >= 0 ? 'text-blue-600' : 'text-orange-600'}`}>
              {formatCurrency(summary.surplus, country)}
            </p>
            <p className="text-xs text-gray-700 dark:text-gray-300 mt-1">
              {summary.savingsRate.toFixed(1)}% savings rate
            </p>
          </div>
        </div>
      )}

      {/* Budget Items by Group */}
      <div className="space-y-4">
        {Object.entries(groupedItems)
          .sort(([a], [b]) => {
            // Sort by custom order first, then fallback to default
            const orderA = groupOrder[a] !== undefined ? groupOrder[a] : 999;
            const orderB = groupOrder[b] !== undefined ? groupOrder[b] : 999;
            if (orderA !== orderB) return orderA - orderB;
            // Fallback to default sort: Income first, then others, Other last
            if (a === 'Income') return -1;
            if (b === 'Income') return 1;
            if (a === 'Other') return 1;
            if (b === 'Other') return -1;
            return a.localeCompare(b);
          })
          .map(([groupName, groupData], groupIndex, allGroups) => {
            const isFirstGroup = groupIndex === 0;
            const isLastGroup = groupIndex === allGroups.length - 1;

            return (
            <div
              key={groupName}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700"
            >
              {/* Group Header */}
              <div className="flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                <button
                  onClick={() => toggleGroup(groupName)}
                  className="flex-1 flex items-center gap-3"
                >
                  {expandedGroups.includes(groupName) ? (
                    <ChevronDown className="w-5 h-5 text-gray-500" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-gray-500" />
                  )}
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{groupName}</h3>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    ({groupData.items.length} items)
                  </span>
                </button>
                <div className="flex items-center gap-2">
                  <p className="text-lg font-semibold text-gray-900 dark:text-white">
                    {formatCurrency(groupData.total, country)}/mo
                  </p>
                  {/* Reorder buttons */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMoveGroup(groupName, 'up');
                    }}
                    disabled={isFirstGroup}
                    className={`p-1.5 rounded transition-colors ${
                      isFirstGroup
                        ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                        : 'text-gray-600 hover:bg-gray-200 dark:text-gray-400 dark:hover:bg-gray-600'
                    }`}
                    title="Move group up"
                  >
                    <ChevronUp className="w-4 h-4" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMoveGroup(groupName, 'down');
                    }}
                    disabled={isLastGroup}
                    className={`p-1.5 rounded transition-colors ${
                      isLastGroup
                        ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                        : 'text-gray-600 hover:bg-gray-200 dark:text-gray-400 dark:hover:bg-gray-600'
                    }`}
                    title="Move group down"
                  >
                    <ChevronDown className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Group Items */}
              {expandedGroups.includes(groupName) && (
                <div className="border-t border-gray-200 dark:border-gray-700">
                  {groupData.items.length > 0 ? (
                    <div className="divide-y divide-gray-100 dark:divide-gray-700">
                      {groupData.items.map(item => (
                        <div
                          key={item.id}
                          className="p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <p className="font-medium text-gray-900 dark:text-white">{item.name}</p>
                                {item.owner && (
                                  <span className="px-2 py-0.5 text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded">
                                    {item.owner}
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                {formatBudgetAmount(item.amount, item.frequency, country === 'UK' ? 'GBP' : 'USD')}
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              <p className="text-lg font-semibold text-gray-900 dark:text-white">
                                {formatCurrency(normalizeToMonthly(item.amount, item.frequency), country)}
                              </p>
                              <button
                                onClick={() => handleEditItem(item)}
                                className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                              >
                                <Edit2 className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteItem(item.id)}
                                className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                      <p>No items in this category</p>
                      <button
                        onClick={() => setShowAddModal(true)}
                        className="mt-2 text-blue-600 hover:underline text-sm"
                      >
                        Add your first item
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
            );
          })}
      </div>

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  {editingItem ? 'Edit Budget Item' : 'Add Budget Item'}
                </h2>
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    setEditingItem(null);
                  }}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <form onSubmit={editingItem ? handleUpdateItem : handleAddItem} className="p-6 space-y-4">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Name
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Monthly Salary, Groceries"
                  required
                />
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Category
                </label>
                <select
                  value={formData.categoryId}
                  onChange={(e) => setFormData({ ...formData, categoryId: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Select a category</option>
                  {Object.entries(
                    categories.reduce((acc, cat) => {
                      if (!acc[cat.group]) acc[cat.group] = [];
                      acc[cat.group].push(cat);
                      return acc;
                    }, {})
                  ).map(([group, cats]) => (
                    <optgroup key={group} label={group}>
                      {cats.map(cat => (
                        <option key={cat.id} value={cat.id}>
                          {cat.name}{cat.isCustom ? ' ⭐' : ''}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>

              {/* Amount */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Amount
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                    {country === 'UK' ? '£' : '$'}
                  </span>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    className="w-full pl-8 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                    placeholder="0.00"
                    required
                  />
                </div>
                {formData.categoryId && categories.find(c => c.id === formData.categoryId)?.type === 'income' && (
                  <div className="mt-2 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                    <p className="text-sm text-blue-900 dark:text-blue-100">
                      <span className="font-medium">Note:</span> Please enter your net income (take-home pay after taxes and deductions), not gross income.
                    </p>
                  </div>
                )}
              </div>

              {/* Frequency */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Frequency
                </label>
                <select
                  value={formData.frequency}
                  onChange={(e) => setFormData({ ...formData, frequency: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                  required
                >
                  {getFrequencyOptions().map(opt => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Owner (optional) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Family Member (Optional)
                </label>
                <input
                  type="text"
                  value={formData.owner}
                  onChange={(e) => setFormData({ ...formData, owner: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., John, Sarah, Kids, Shared"
                  list="owner-suggestions"
                />
                <datalist id="owner-suggestions">
                  {[...new Set(budgetItems.map(item => item.owner).filter(o => o))].map(owner => (
                    <option key={owner} value={owner} />
                  ))}
                </datalist>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Track spending by family member name
                </p>
              </div>

              {/* Monthly Equivalent Preview */}
              {formData.amount && formData.frequency && (
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                  <p className="text-sm text-blue-900 dark:text-blue-100">
                    <span className="font-medium">Monthly equivalent:</span>{' '}
                    {formatCurrency(
                      normalizeToMonthly(parseFloat(formData.amount) || 0, formData.frequency),
                      country
                    )}
                  </p>
                </div>
              )}

              {/* Buttons */}
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    setEditingItem(null);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                >
                  <Save className="w-4 h-4" />
                  {editingItem ? 'Update' : 'Add'} Item
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Category Manager Modal */}
      {showCategoryManager && (
        <CategoryManager
          onClose={() => setShowCategoryManager(false)}
          onCategoryAdded={() => {
            loadCategories();
            loadBudgetData();
          }}
        />
      )}
    </div>
  );
};

export default MonthlyBudget;
